import { useState, useEffect } from 'react';
import Form from 'react-bootstrap/Form';
import Navbar from '../components/Navbar';
import * as client from '../api/client';
import '../styles/styles.css'
import { Button } from 'react-bootstrap';
import { PlainDate, sortProductInstances } from '../utils/utils';

const userRoleIdToFetch = "df177434-cdbc-44cf-99a9-e6ecf72bb626";

const ProductionTable = () => {
    const [currentMonthYear, setCurrentMonthYear] = useState(null);
    const [productInstances, setProductInstances] = useState([]);
    const [products, setProducts] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [users, setUsers] = useState(null);
    const [editableData, setEditableData] = useState({});
    const [isSaving, setIsSaving] = useState(false);
    const [isEditingTable, setIsEditingTable] = useState(false);
    const [selectedUser, setSelectedUser] = useState(null);
    const [changedCells, setChangedCells] = useState({});

    useEffect(() => {
        const currentDate = new Date();
        setCurrentMonthYear(new PlainDate(currentDate.getFullYear(), currentDate.getMonth() + 1, currentDate.getDate()));

        const fetchUsers = async () => {
            const employees = await client.getUsersByRole(userRoleIdToFetch);
            setUsers(employees);
            setSelectedUser(employees[0]);
        }
        fetchUsers();
    }, []);

    useEffect(() => {
        if (selectedUser == null) {
            return;
        }
        const fetchData = async () => {
            setLoading(true);
            setError(null);

            try {
                const productInstancesData = await client.getProductInstances(selectedUser.id, currentMonthYear.getMonth(), currentMonthYear.getYear());
                setProductInstances(productInstancesData);

                if (products.length === 0) {
                    const products = await client.getProductNames();
                    setProducts(products);
                }

                setLoading(false);
            } catch (err) {
                setError(err.message);
                setLoading(false);
            }
        };

        fetchData();
    }, [selectedUser, currentMonthYear]);

    useEffect(() => {
        const initialEditableData = {};
        productInstances.forEach(productInstance => {
            const creationDate = PlainDate.fromString(productInstance.creationDate);

            if (
                creationDate.getMonth() === currentMonthYear.getMonth() &&
                creationDate.getYear() === currentMonthYear.getYear()
            ) {
                if (!initialEditableData[productInstance.productId]) {
                    initialEditableData[productInstance.productId] = {};
                }

                const day = creationDate.getDay();
                if (!Number.isNaN(day)) {
                    initialEditableData[productInstance.productId][day] =
                        Number(productInstance.quantity) || 0;
                }
            }
        });
        setEditableData(initialEditableData);
    }, [productInstances, currentMonthYear]);

    const handleCellChange = (productId, day, value) => {
        setEditableData(prev => ({
            ...prev,
            [productId]: {
                ...prev[productId],
                [day]: Number(value) || 0
            }
        }));

        setChangedCells(prev => ({
            ...prev,
            [productId]: {
                ...prev[productId],
                [day]: true
            }
        }));
    };

    const saveData = async () => {
        setIsSaving(true);
        try {
            const existingEntriesMap = productInstances.reduce((acc, entry) => {
                const entryDate = PlainDate.fromString(entry.creationDate);
                if (entryDate.getMonth() === currentMonthYear.getMonth() && entryDate.getYear() === currentMonthYear.getYear()) {
                    const day = entryDate.getDay();
                    if (!acc[entry.productId]) {
                        acc[entry.productId] = {};
                    }
                    acc[entry.productId][day] = entry.id;
                }
                return acc;
            }, {});

            const updates = [];
            Object.keys(editableData).forEach(productId => {
                Object.keys(editableData[productId]).forEach(day => {
                    const entryId = existingEntriesMap[productId]?.[day];
                    updates.push({
                        id: entryId || null,
                        product: { id: productId },
                        creationDate: new PlainDate(
                            currentMonthYear.getYear(),
                            currentMonthYear.getMonth(),
                            Number(day)
                        ).toString(),
                        quantity: editableData[productId][day],
                        userId: selectedUser.id
                    });
                });
            });

            await client.bulkApplyProductInstances(updates);

            setChangedCells({});
        } catch (error) {
            console.error('Save error:', error);
            setError(error.message || 'Failed to save changes');
        } finally {
            setIsSaving(false);
            setIsEditingTable(false);
        }
    };

    const enterEditMode = async () => {
        setIsEditingTable(true);
    }

    const changeMonth = (offset) => {
        if (offset > 0) {
            setCurrentMonthYear(currentMonthYear.nextMonth())
        } else {
            setCurrentMonthYear(currentMonthYear.previousMonth())
        }
        setChangedCells({});
    };

    if (loading) return <div className="loading">Loading production data...</div>;
    if (error) return <div className="error">Error: {error}</div>;
    const daysInMonth = new Date(currentMonthYear.getYear(), currentMonthYear.getMonth(), 0).getDate();
    const daysArray = Array.from({ length: daysInMonth }, (_, i) => i + 1);
    const productMap = products.reduce((acc, product) => {
        acc[product.id] = product.name;
        return acc;
    }, {});

    const productInstancesArray = [
        ...productInstances,
        ...products
            .filter(p => !productInstances.some(i => i.productId === p.id))
            .map(({ id }) => ({ productId: id }))
    ];

    const aggregatedByProduct = productInstancesArray.reduce((acc, productInstance) => {
        const id = productInstance.productId;

        if (!acc[id]) {
            acc[id] = {
                id: id,
                name: productMap[id] || 'Unknown Product',
                days: {}
            };
        }

        if (productInstance.creationDate) {
            const creationDate = PlainDate.fromString(productInstance.creationDate);
            const day = Number.isNaN(creationDate.getDay()) ? null : creationDate.getDay();

            if (day !== null) {
                const qty = Number(productInstance.quantity) || 0;
                acc[id].days[day] = (acc[id].days[day] || 0) + qty;
            }
        }

        return acc;
    }, {});

    var productInstancesData = Object.values(aggregatedByProduct);
    productInstancesData = sortProductInstances(productInstancesData);

    return (
        <div id="prod-table">
            <Navbar />
            <div className="production-table">
                <div className="table-header">
                    <h2>
                        <Form.Select
                            value={selectedUser?.id ?? ""}
                            onChange={e => {
                                const user = users.find(u => String(u.id) === e.target.value);
                                setSelectedUser(user || null);
                            }}>
                            {users.map(u => (
                                <option key={u.id} value={String(u.id)}>
                                    {u.name}
                                </option>
                            ))}
                        </Form.Select>
                    </h2>
                    <div className="date-selector">
                        <button onClick={() => changeMonth(-1)}>&lt;</button>
                        <h2>
                            {currentMonthYear.toDate().toLocaleDateString('ru-RU', {
                                month: 'long',
                                year: 'numeric'
                            })}
                        </h2>
                        <button onClick={() => changeMonth(1)}>&gt;</button>
                    </div>
                    <div className="save-edit-controls">
                        <Button variant="primary" onClick={saveData} disabled={isSaving || !isEditingTable} className="save-button">
                            {isSaving ? 'Сохраняется...' : 'Сохранить'}
                        </Button>
                        <Button variant="secondary" onClick={enterEditMode} disabled={isEditingTable} className="save-button">
                            {isEditingTable ? 'Редактировать' : 'Редактировать'}
                        </Button>
                    </div>
                </div>

                <div className="table-content">
                    <table>
                        <thead>
                            <tr>
                                <th>Наименование</th>
                                {daysArray.map(day => (
                                    <th key={day}>{day}</th>
                                ))}
                                <th>Итого</th>
                            </tr>
                        </thead>
                        <tbody>
                            {productInstancesData.length > 0 ? (
                                productInstancesData.map(product => (
                                    <tr key={product.name}>
                                        <td>{product.name}</td>
                                        {daysArray.map(day => (
                                            <td key={day}>
                                                <input className={changedCells[product.id]?.[day] ? "changed-cell" : "editable-cell"}
                                                    type="number"
                                                    min="0"
                                                    value={editableData[product.id]?.[day] || 0}
                                                    onChange={(e) => handleCellChange(product.id, day, e.target.value)}
                                                    disabled={!isEditingTable}
                                                />
                                            </td>
                                        ))}
                                        <td>
                                            {Object.values(editableData[product.id] || {}).reduce((sum, val) => sum + Number(val || 0), 0)}
                                        </td>
                                    </tr>
                                ))
                            ) : (
                                <tr>
                                    <td colSpan={daysArray.length + 1}>
                                        No production data available for this month
                                    </td>
                                </tr>
                            )}
                        </tbody>
                    </table>
                </div>
            </div>
        </div>
    );
};

export default ProductionTable;